// src/components/DashboardExecutiveSummary.jsx
//
// "Executive Summary" tab on the portfolio-level Dashboard. Two cascading
// filters -- OpCo, then Project ID (dependent on the selected OpCo) -- let
// an exec drill into a single project's Budget vs Estimation breakdown,
// reusing the same chart/table treatment already built for the per-project
// Executive Summary page (EstimationSummary.jsx / GET
// /get_project_estimations_api). Kept as its own file rather than folded
// into EstimationSummary.jsx: that page reads its project from
// localStorage (the wizard's "current project"), this one reads it from
// the two dropdowns below -- different data source, same visual result.
//
// Per request, nothing selected is no longer a dead end: before either
// dropdown is touched, the page shows a summary table of every completed
// project's cost estimation details (any OpCo) instead of just asking the
// user to pick something first. Picking an OpCo + Project ID still
// switches to the detailed single-project chart/breakdown exactly as
// before -- see allSummaryRows below for how the default table is built.
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
  Title
} from "chart.js";
import { api as axios, API_ORIGIN } from "../config/apiConfig";
import { ROUTES } from "../config/routes";

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend, Title);

const API_BASE = API_ORIGIN;

// Same 7-step indicator as EstimationSummary.jsx's step bar -- shown
// here too, per request, once a single project is drilled into (below).
// All-green/"done" and fully clickable, same as there -- opens each
// step's separate READ-ONLY page (Step1ProjectInfoReadOnly.jsx, etc.),
// never the real editable Step1.../BasisOfCost/ElementScopePage1 pages,
// so this never touches the Cost Estimation create/edit wizard.
const WIZARD_STEPS = [
  { key: "project", icon: "ti-file-description", label: "Project", sub: "Info & Overview" },
  { key: "budget", icon: "ti-currency-riyal", label: "Budget", sub: "Amount & Summary" },
  { key: "asset", icon: "ti-building", label: "Asset", sub: "Selection & Element" },
  { key: "element", icon: "ti-list-check", label: "Element", sub: "Main & Sub Element" },
  { key: "estimation", icon: "ti-report-money", label: "Estimation", sub: "Amount & Summary" },
  { key: "basis", icon: "ti-clipboard-text", label: "Basis of Cost", sub: "Assumption & Exclusion" },
  { key: "construction", icon: "ti-hammer", label: "Construction", sub: "Scope" },
];

// Each WIZARD_STEPS key -> its READ-ONLY route -- same mapping
// EstimationSummary.jsx uses.
const STEP_ROUTES = {
  project: ROUTES.COST_ESTIMATION_STEP1_VIEW,
  budget: ROUTES.COST_ESTIMATION_STEP2_VIEW,
  asset: ROUTES.COST_ESTIMATION_STEP3_VIEW,
  element: ROUTES.COST_ESTIMATION_STEP4_VIEW,
  estimation: ROUTES.COST_ESTIMATION_STEP5_VIEW,
  basis: ROUTES.BASIS_OF_COST_VIEW,
  construction: ROUTES.ELEMENT_SCOPE_PAGE1_VIEW,
};

// Same categories / colors / gradient treatment as EstimationSummary.jsx's
// "Budget Vs Estimation" chart, kept in sync by construction since both
// read from the same /get_project_estimations_api response shape.
const CATEGORIES = [
  // Per request, display label changed from "Construction" to "Execution"
  // -- key stays "Construction" since that's the raw field name the
  // backend (summary.budget/summary.estimated) still sends.
  { key: "Construction", label: "Execution", percentKey: null },
  { key: "Design", label: "Design", percentKey: null },
  { key: "Supervision", label: "Supervision", percentKey: null },
  { key: "Other_Project", label: "Other Project", percentKey: null },
  { key: "Prelims_Amount", label: "Prelims", percentKey: "Prelims_Percent" },
  { key: "Risk_Amount", label: "Risk", percentKey: "Risk_Percent" },
  { key: "Inflation_Amount", label: "Inflation", percentKey: "Inflation_Percent" },
  { key: "Contingency_Amount", label: "Contingency", percentKey: "Contingency_Percent" },
  { key: "Location_Amount", label: "Location", percentKey: "Location_Percent" },
  { key: "Tender_Amount", label: "Tender", percentKey: "Tender_Percent" },
  { key: "Escalation_Amount", label: "Escalation", percentKey: "Escalation_Percent" }
];

const COLOR_BUDGET = "#2a78d6";
const COLOR_ESTIMATED = "#1baf7a";
const COLOR_MOVEMENT = "#e34948";

const barGradient = (hex) => (ctx) => {
  const { chart } = ctx;
  const { chartArea } = chart;
  if (!chartArea) return hex;
  const g = chart.ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
  g.addColorStop(0, hex);
  g.addColorStop(1, `${hex}b3`);
  return g;
};

const fmt = (n) => Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
// FIX: this used to just append the letter "M" to the full, undivided
// number (e.g. 1,360,000,000 -> "1,360,000,000.00M"), which only ever
// looked like millions because of the label, not the value. Per explicit
// request (KPI tiles / tooltip / table, matching the Y-axis fix below),
// the value itself is now actually divided by 1,000,000 first, with a
// space before the unit (e.g. 1,360,000,000 -> "1,360.00 M") -- same
// 2-decimal precision as before, just on the real millions figure
// instead of the raw one.
const fmtM = (n) => `${fmt(Number(n || 0) / 1000000)} M`;

// Print Report's OpCo logo (see handlePrintReport's coverHtml, further
// down) needs to sit directly on the cover's dark teal background with
// no box behind it and every part of the logo -- regardless of its own
// original colors -- the same solid white. A CSS filter can't do that
// selectively: brightness(0) invert(1) treats every opaque pixel (the
// logo's own background canvas AND its ink) identically, so on a logo
// that isn't transparent-background (most real uploaded ones, e.g. the
// "cluster2" OpCo's) it either shows the logo in its true colors inside
// a visible white chip, or flattens the whole box to one indistinguishable
// white rectangle -- see this function's own use below for the full
// history. This instead decodes the logo onto an offscreen canvas and
// repaints it pixel-by-pixel: bright/near-white pixels (the logo's own
// background, however opaque) become fully transparent, everything else
// (any original hue) becomes solid white, with a soft luminance ramp
// (not a hard cutoff) so anti-aliased edges stay smooth. Resolves null
// (caller falls back to the plain OpCo-name text) if the image can't be
// decoded for any reason.
function toWhiteLogoSilhouette(srcDataUrl) {
  return new Promise((resolve) => {
    if (!srcDataUrl) {
      resolve(null);
      return;
    }
    const img = new Image();
    img.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = img.naturalWidth || img.width;
        canvas.height = img.naturalHeight || img.height;
        if (!canvas.width || !canvas.height) {
          resolve(null);
          return;
        }
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0);
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const px = imageData.data;
        for (let i = 0; i < px.length; i += 4) {
          const r = px[i];
          const g = px[i + 1];
          const b = px[i + 2];
          const a = px[i + 3];
          // Standard perceptual luminance. Pixels at/above ~235 (the
          // logo's own white/near-white canvas) fade fully to
          // transparent; pixels at/below ~200 (actual ink, any color)
          // stay fully opaque; the 35-unit band between is a soft ramp
          // so anti-aliased edges don't turn jagged.
          const luminance = 0.299 * r + 0.587 * g + 0.114 * b;
          const ink = Math.min(1, Math.max(0, (235 - luminance) / 35));
          px[i] = 255;
          px[i + 1] = 255;
          px[i + 2] = 255;
          px[i + 3] = a * ink;
        }
        ctx.putImageData(imageData, 0, 0);
        resolve(canvas.toDataURL("image/png"));
      } catch (err) {
        console.error("Failed to convert OpCo logo to a white silhouette", err);
        resolve(null);
      }
    };
    img.onerror = () => resolve(null);
    img.src = srcDataUrl;
  });
}
const fmtPct = (n) => `${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`;
// Y-axis tick labels: same millions conversion as fmtM, but rounded to a
// whole number (no decimals) since the axis only needs coarse gridline
// labels, e.g. 2,000,000,000 -> "2,000 M".
const fmtAxisMillions = (n) =>
  `${(Number(n || 0) / 1000000).toLocaleString(undefined, { maximumFractionDigits: 0 })} M`;

// Same duration formatter as EstimationSummary.jsx's OpCo/Duration tiles
// (kept in sync by construction, same as CATEGORIES/COLOR_* above) --
// Duration_Master's seed values are "12 Months" .. "60 Months (5 Years)",
// this always renders the plain "N Years" form regardless of which of
// those two source shapes the saved value is in.
const formatDurationYears = (raw) => {
  if (raw === null || raw === undefined || raw === "") return "—";
  const str = String(raw).trim();
  if (!str) return "—";
  const parenYearsMatch = str.match(/\((\d+(\.\d+)?)\s*years?\)/i);
  if (parenYearsMatch) {
    const y = Number(parenYearsMatch[1]);
    return `${y} Year${y === 1 ? "" : "s"}`;
  }
  if (/year/i.test(str) && !/month/i.test(str)) return str;
  const match = str.match(/(\d+(\.\d+)?)/);
  if (!match) return str;
  const months = Number(match[1]);
  if (!months) return str;
  const years = months / 12;
  const roundedYears = Number.isInteger(years) ? years : Math.round(years * 10) / 10;
  return `${roundedYears} Year${roundedYears === 1 ? "" : "s"}`;
};

// OpCo / Project ID selection, and the resulting project list, all now
// live in the parent (Dashboard.jsx) so its tab-pill row can render the
// two filter dropdowns on the right side (see that file) -- this
// component just consumes the current selection via props and stays
// purely presentational for the summary tiles/chart/table below, plus
// its own "every completed project" landing table.
export default function DashboardExecutiveSummary({
  selectedOpco,
  selectedOpcoName,
  selectedProjectId,
  projectList,
  projectLoading,
  onSelectRow
}) {
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryError, setSummaryError] = useState(null);

  // Per request, this tab now has the same "Print Report" button as the
  // per-project EstimationSummary.jsx page -- toBase64Image() needs a
  // ref straight to the underlying Chart.js instance for a real snapshot
  // of the chart as rendered, same trick used there.
  const chartRef = useRef(null);

  // Same three best-effort fetches EstimationSummary.jsx makes for its
  // Print Report -- Project Overview / Basis of Cost / Elemental
  // Construction Scope + Other Scope narrative fields -- keyed here off
  // the selectedProjectId prop instead of a localStorage projectID.
  const [projectExtra, setProjectExtra] = useState(null);
  const [basisData, setBasisData] = useState(null);
  const [elementScopeData, setElementScopeData] = useState(null);
  // Every saved Sub Element row for this project (project_sub_elements),
  // per explicit request: shown as its own table on the print report's
  // Basis of Cost page, right before the Drawings table.
  const [subElementsData, setSubElementsData] = useState([]);

  // Currency symbol for the chart's Y-axis title -- same Currency Master
  // table (GET /currency) every other Cost Estimation page reads, instead
  // of a hard-coded literal, so changing that table's Currency_Symbol
  // value changes what shows here with no code change. Matches
  // EstimationSummary.jsx / Step2RiskInflation.jsx's identical fetch.
  const [currencySymbol, setCurrencySymbol] = useState("");

  useEffect(() => {
    axios
      .get(`${API_BASE}/currency`)
      .then((res) => setCurrencySymbol(res.data?.data?.Currency_Symbol || ""))
      .catch(() => {});
  }, []);

  // Per request, the page no longer just tells the user to pick an OpCo
  // and Project ID before showing anything -- by default (nothing picked
  // yet) it shows a summary table of EVERY completed project's cost
  // estimation details across all Operating Companies, fetched once on
  // mount. GET /projects has no batch "give me every completed project's
  // Budget vs Estimation totals" endpoint, so this fetches the completed
  // project list (no opco_id filter = all OpCos) and then calls the same
  // GET /get_project_estimations_api used for the single-project view
  // once per project, in parallel. Once the user actually picks an OpCo
  // + Project ID, the existing single-project chart/table below takes
  // over exactly as before -- this table is only the default landing
  // view, not a replacement for that drill-down.
  const [allSummaryRows, setAllSummaryRows] = useState([]);
  const [allSummaryLoading, setAllSummaryLoading] = useState(true);
  const [allSummaryError, setAllSummaryError] = useState(null);

  // Per request, once an OpCo is picked up in Dashboard.jsx's tab-pill
  // row (e.g. "MATARAT"), the landing table below narrows to that OpCo's
  // own completed projects instead of always showing every OpCo's --
  // matches each row's opco_name (from GET /projects) against the
  // selected OpCo's display name. Nothing selected still shows every
  // completed project across every OpCo, same as before.
  const visibleSummaryRows = selectedOpcoName
    ? allSummaryRows.filter(
        (row) => (row.opco_name || "").trim().toLowerCase() === selectedOpcoName.trim().toLowerCase()
      )
    : allSummaryRows;

  // Default "all cost estimation details" table -- every completed
  // project, any OpCo, fetched once on mount. See the note above
  // allSummaryRows's declaration for why this needs one call per
  // project rather than a single batch endpoint.
  useEffect(() => {
    let cancelled = false;
    setAllSummaryLoading(true);
    setAllSummaryError(null);
    axios
      .get(`${API_BASE}/projects?pageSize=100`)
      .then((res) => {
        const all = res.data?.data || [];
        const completed = all.filter((p) => (p.status || "").trim().toUpperCase() === "C");
        return Promise.all(
          completed.map((p) =>
            axios
              .get(`${API_BASE}/get_project_estimations_api?projectId=${encodeURIComponent(p.projectid)}`)
              .then((r) => (r.data?.success ? { project: p, summary: r.data } : null))
              .catch(() => null)
          )
        );
      })
      .then((results) => {
        if (cancelled) return;
        const rows = results
          .filter(Boolean)
          .map(({ project, summary: s }) => {
            const budgetTotal = Number(s.budget?.Total_Sum || 0);
            const estimatedTotal = Number(s.estimated?.Total_Sum || 0);
            return {
              projectid: project.projectid,
              project_name: project.project_name,
              opco_name: project.opco_name,
              budgetTotal,
              estimatedTotal,
              movement: estimatedTotal - budgetTotal
            };
          });
        setAllSummaryRows(rows);
      })
      .catch(() => {
        if (!cancelled) setAllSummaryError("Failed to load cost estimation details.");
      })
      .finally(() => {
        if (!cancelled) setAllSummaryLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // OpCo/Project ID selection and the resulting project list are now
  // props from Dashboard.jsx (see that file) instead of state owned here.

  // Budget vs Estimation summary -- fetched once a project is picked.
  // Re-runs (and clears first) whenever selectedProjectId changes,
  // including back to "" -- e.g. when the OpCo dropdown changes upstream
  // in Dashboard.jsx, which resets selectedProjectId there too.
  useEffect(() => {
    setSummary(null);
    setSummaryError(null);
    if (!selectedProjectId) return;
    setSummaryLoading(true);
    axios
      .get(`${API_BASE}/get_project_estimations_api?projectId=${encodeURIComponent(selectedProjectId)}`)
      .then((res) => {
        if (!res.data?.success) throw new Error(res.data?.error || "Failed to load summary");
        setSummary(res.data);
      })
      .catch((err) => setSummaryError(err.message || "Failed to load the summary for this project."))
      .finally(() => setSummaryLoading(false));
  }, [selectedProjectId]);

  // Print Report's extra sections -- same three GET endpoints and same
  // file-to-drawing-row matching logic as EstimationSummary.jsx (see
  // that file's matching effect for the full rationale); a project that
  // never reached a given step just leaves that state null, and the
  // report skips any section with nothing real to show.
  useEffect(() => {
    setProjectExtra(null);
    setBasisData(null);
    setElementScopeData(null);
    setSubElementsData([]);
    if (!selectedProjectId) return;

    axios
      .get(`${API_BASE}/projects?search=${encodeURIComponent(selectedProjectId)}&pageSize=20`)
      .then((res) => {
        const rows = res.data?.data || [];
        const match = rows.find((r) => String(r.projectid) === String(selectedProjectId)) || rows[0] || null;
        setProjectExtra(match);
      })
      .catch((err) => console.error("Failed to load project overview fields", err));

    axios
      .get(`${API_BASE}/basis_of_cost_api?Projectid=${encodeURIComponent(selectedProjectId)}`)
      .then((res) => {
        const json = res.data;
        if (json?.success && json?.found) {
          // Match each uploaded file to its Drawing Name/No row, same
          // logic as BasisOfCostReadOnly.jsx -- lets the print report
          // know which row(s) have an attached drawing file to embed.
          const items = Array.isArray(json.items) ? json.items.map((it) => ({ ...it })) : [];
          if (Array.isArray(json.files) && json.files.length) {
            const remainingFiles = [...json.files];
            items.forEach((row) => {
              const idx = remainingFiles.findIndex(
                (f) =>
                  (f.drawingName || "").trim() === (row.drawingName || "").trim() &&
                  (f.drawingNo || "").trim() === (row.drawingNo || "").trim()
              );
              if (idx !== -1) {
                const f = remainingFiles.splice(idx, 1)[0];
                row.attachedFile = { basisId: json.basis_id, filename: f.filename };
              }
            });
          }
          setBasisData({ ...json, items });
        }
      })
      .catch((err) => console.error("Failed to load basis of cost", err));

    axios
      .get(`${API_BASE}/element_scope?Projectid=${encodeURIComponent(selectedProjectId)}`)
      .then((res) => {
        const json = res.data;
        if (json?.success && json?.data) setElementScopeData(json.data);
      })
      .catch((err) => console.error("Failed to load element scope", err));

    axios
      .get(`${API_BASE}/get_project_sub_elements?project_id=${encodeURIComponent(selectedProjectId)}`)
      .then((res) => {
        const json = res.data;
        if (json?.success && Array.isArray(json.data)) setSubElementsData(json.data);
      })
      .catch((err) => console.error("Failed to load sub elements", err));
  }, [selectedProjectId]);

  // Clicking a step tab below -- this page doesn't otherwise read/write
  // localStorage (its project comes from the selectedProjectId prop, via
  // Dashboard.jsx's own OpCo/Project dropdowns), but the read-only step
  // pages it's linking to still expect a project id in localStorage the
  // same way EstimationSummary.jsx's tabs already provide one. Both keys
  // are set since different step pages were built off different
  // originals ("projectID" vs "project_db_id") -- harmless to set both.
  const goToStep = (stepKey) => {
    if (selectedProjectId) {
      localStorage.setItem("projectID", selectedProjectId);
      localStorage.setItem("project_db_id", selectedProjectId);
    }
    navigate(STEP_ROUTES[stepKey]);
  };

  const budgetSeries = summary ? CATEGORIES.map((c) => Number(summary.budget[c.key] || 0)) : [];
  const estimatedSeries = summary ? CATEGORIES.map((c) => Number(summary.estimated[c.key] || 0)) : [];
  const movementSeries = summary ? budgetSeries.map((b, i) => Number(estimatedSeries[i] - b)) : [];
  const budgetPercentSeries = summary
    ? CATEGORIES.map((c) => (c.percentKey ? Number(summary.budget[c.percentKey] || 0) : null))
    : [];
  const estimatedPercentSeries = summary
    ? CATEGORIES.map((c) => (c.percentKey ? Number(summary.estimated[c.percentKey] || 0) : null))
    : [];
  const budgetTotal = summary ? Number(summary.budget.Total_Sum || 0) : 0;
  const estimatedTotal = summary ? Number(summary.estimated.Total_Sum || 0) : 0;
  const movement = estimatedTotal - budgetTotal;

  const chartData = {
    labels: CATEGORIES.map((c) => c.label),
    datasets: [
      // Per request, "Budget Cost" is displayed as "Proposed Cost"
      // throughout this page (KPI tile, chart legend/dataset label, chart
      // title, table headers/rows) -- matches EstimationSummary.jsx's and
      // Step2RiskInflation.jsx's "Proposed Cost" naming. Internal
      // variable/DB names (budgetSeries, budgetTotal, etc.) are left
      // as-is -- only the user-facing text changed.
      { label: "Proposed Cost", data: budgetSeries, backgroundColor: barGradient(COLOR_BUDGET), hoverBackgroundColor: COLOR_BUDGET, maxBarThickness: 22 },
      { label: "Estimated Cost", data: estimatedSeries, backgroundColor: barGradient(COLOR_ESTIMATED), hoverBackgroundColor: COLOR_ESTIMATED, maxBarThickness: 22 }
      // Per explicit request, the "Movement" bar/dataset (and, since
      // Chart.js builds its legend straight from chartData.datasets, its
      // legend entry too) is removed from this chart -- Movement still
      // shows in the data table below unchanged, this only affects the
      // bar chart itself.
    ]
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    layout: { padding: { top: 8 } },
    plugins: {
      legend: {
        position: "top",
        // Per explicit request, centered above the chart (was "start",
        // i.e. left-aligned) -- Chart.js's legend.align accepts
        // "start"/"center"/"end" for a "top" position.
        align: "center",
        labels: { usePointStyle: true, pointStyle: "circle", boxWidth: 12, boxHeight: 12, padding: 16, font: { size: 16 } }
      },
      title: { display: false },
      tooltip: {
        backgroundColor: "#124d52",
        titleFont: { size: 15, weight: "600" },
        bodyFont: { size: 15 },
        padding: 10,
        cornerRadius: 6,
        displayColors: true,
        boxPadding: 4,
        callbacks: {
          // Per request, matches the "M" suffix convention already used by
          // the KPI tiles/table (fmtM) instead of the raw full-precision
          // number this tooltip used to show.
          label: (item) => ` ${item.dataset.label}: ${fmtM(item.raw)}`
        }
      }
    },
    scales: {
      x: {
        title: { display: true, text: "Cost Breakdown", font: { size: 17, weight: "bold" }, color: "#52514e" },
        ticks: { maxRotation: 0, autoSkip: true, font: { size: 15, weight: "bold" }, color: "#52514e" },
        grid: { display: false },
        border: { color: "#c3c2b7" },
        barPercentage: 0.85,
        categoryPercentage: 0.7
      },
      y: {
        // Per request, bumped up from 12/10 -- the Y-axis title and tick
        // labels were rendering too small/cramped once fmtM's longer
        // "...M" strings replaced the plain numbers here.
        title: { display: true, text: `Cost (${currencySymbol})`, font: { size: 18, weight: "bold" }, color: "#52514e" },
        ticks: {
          // Per explicit request, the axis itself now shows the value
          // actually converted to millions (e.g. 2,000,000,000 -> "2,000 M")
          // instead of fmtM's old behavior of just appending "M" to the raw
          // number (which is what the tooltip/KPI tiles/table below still
          // do, unchanged, since only the axis was asked for here).
          callback: (val) => fmtAxisMillions(val),
          font: { size: 15, weight: "bold" },
          color: "#898781"
        },
        grid: { color: "#e1e0d9" },
        border: { display: false },
        // Without this, Chart.js scales the axis right up to the tallest
        // bar's exact value, so the biggest column visually touches the
        // plot area's top edge. `grace` pads the axis range by 10% of the
        // data's span above (and below) the actual min/max, same fix as
        // EstimationSummary.jsx's identical chart.
        grace: "10%"
      }
    },
    interaction: { mode: "index", intersect: false },
    elements: { bar: { borderRadius: 4, borderSkipped: "start" } }
  };

  // Per request, same "Print Report" feature as the per-project
  // EstimationSummary.jsx page -- Cover, Executive Summary, Project
  // Overview, Basis of Cost, Elemental Construction Scope, Other Scope
  // and a closing back page, using this page's own summary/
  // projectExtra/basisData/elementScopeData state instead of that page's
  // (this page reads its project from the selectedProjectId prop/
  // dropdowns rather than localStorage -- everything else is identical,
  // kept in sync by construction the same way the chart/table above
  // already is).
  const handlePrintReport = async () => {
    // FIX: window.open() used to be called at the very end of this
    // function, after the drawing-file Promise.all below (a real network
    // round trip). Opening a popup that late -- well after the click
    // handler's synchronous "user activation" window has closed -- is
    // exactly what several browsers' popup blockers key on: instead of
    // outright refusing it (which would have hit the `if (!win) alert(...)`
    // guard), they let window.open() return a real window handle but treat
    // it as untrusted/backgrounded, so it shows up as a blank tab that
    // never actually receives the document.write() below and can leave
    // the tab (and sometimes the opener) looking unresponsive until it's
    // closed. Opening it here, synchronously, in the very first tick of
    // the click handler -- before any await -- keeps it inside the
    // trusted user-gesture window every browser honors, so the write/
    // print at the end of this function actually lands in it.
    const win = window.open("", "_blank");
    if (!win) {
      alert("Please allow pop-ups for this site to print the report.");
      return;
    }
    // Immediate placeholder so the new tab never just looks blank/frozen
    // while the drawing-file fetch below is in flight -- document.open()
    // right before the real write() further down clears this out first,
    // so the two writes don't get jumbled together into one document.
    win.document.write(
      `<!doctype html><title>Cost Estimation Report</title><body style="font-family:sans-serif;padding:60px;color:#666;">Generating report&hellip;</body>`
    );

    const chartImg =
      chartRef.current && typeof chartRef.current.toBase64Image === "function"
        ? chartRef.current.toBase64Image()
        : null;

    const logoUrl = `${window.location.origin}/assets/images/logo/CET-Full-icon.png`;
    const mataratLogoUrl = `${window.location.origin}/assets/images/logo/matarat_logo.png`;
    // See toWhiteLogoSilhouette's own comment (top of file) for why this
    // replaces the earlier white "chip" background -- per explicit
    // request, the OpCo logo now sits directly on the dark cover with no
    // box behind it.
    const opcoLogoUrl = summary.operatingCompanyLogo ? await toWhiteLogoSilhouette(summary.operatingCompanyLogo) : null;
    const generatedOn = new Date().toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });
    const reportYear = new Date().getFullYear();

    const esc = (s) =>
      String(s ?? "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;");

    // Per request: users often paste multi-level bulleted text (Word-style
    // "•"/"o"/"*" bullets) into these plain-textarea fields; a leading
    // bullet character from the pasted text is stripped before
    // escaping/rendering.
    const stripBulletPrefix = (line) => line.replace(/^(?:[•*]|o)\s+/, "");
    // REVERTED, per follow-up request ("dont start any no 1,2,3,4,5,6",
    // then "start with . dot", then "remove . strart dot"): these
    // multi-line fields get no auto-added marker at all now -- each line
    // just renders as its own plain line, with only whatever the user
    // actually typed.
    const renderTextBlock = (text) => {
      const t = (text || "").toString().trim();
      if (!t) return "";
      const lines = t.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
      if (lines.length > 1) {
        return `<div class="text-list">${lines.map((l) => `<div class="text-line">${esc(stripBulletPrefix(l))}</div>`).join("")}</div>`;
      }
      return `<p>${esc(stripBulletPrefix(t))}</p>`;
    };
    const section = (label, text) => {
      const html = renderTextBlock(text);
      return html ? `<div class="subsection"><h3>${esc(label)}</h3>${html}</div>` : "";
    };

    // "Project Introduction" is now edited with RichTextEditor.jsx (a
    // contentEditable field), so its saved value is real HTML
    // (bold/italic/underline/lists), not plain text -- running it
    // through renderTextBlock()/esc() above would print the literal
    // "<b>" tags instead of bold text. sanitizeRichHtml defensively
    // strips anything unsafe before it's written into the print
    // window's document.
    const sanitizeRichHtml = (html) => {
      if (!html || typeof document === "undefined") return "";
      const container = document.createElement("div");
      container.innerHTML = html;
      container.querySelectorAll("script, style, iframe, object, embed, link, meta").forEach((el) => el.remove());
      // FIX, per follow-up ("underline text have bold but report not
      // showing"): when a run has BOTH bold and underline applied (e.g.
      // a "1. Engineering Assessment and Planning" heading), Chrome's
      // execCommand doesn't always wrap it in a <b>/<strong> tag -- it
      // can instead add font-weight directly as an inline style on the
      // element (alongside the <u> tag, which IS a real tag and was
      // printing fine). The old code stripped the whole "style"
      // attribute except text-align, so that inline
      // font-weight/italic/underline styling was silently thrown away
      // and the text printed underlined-only, not bold. Now a small
      // safe whitelist of formatting properties is kept (in addition to
      // real <b>/<i>/<u> tags, which were never affected).
      const SAFE_STYLE_PROPS = ["textAlign", "fontWeight", "fontStyle", "textDecoration", "textDecorationLine"];
      container.querySelectorAll("*").forEach((el) => {
        const keep = {};
        if (el.style) {
          SAFE_STYLE_PROPS.forEach((prop) => {
            const v = el.style[prop];
            if (v) keep[prop] = v;
          });
        }
        [...el.attributes].forEach((attr) => {
          if (/^on/i.test(attr.name) || attr.name === "style" || attr.name === "href" || attr.name === "src") {
            el.removeAttribute(attr.name);
          }
        });
        Object.keys(keep).forEach((prop) => {
          el.style[prop] = keep[prop];
        });
      });
      return container.innerHTML;
    };
    const richTextSection = (label, html) => {
      const clean = sanitizeRichHtml(html);
      const isEmpty = !clean || clean.replace(/<[^>]+>/g, "").trim() === "";
      return isEmpty ? "" : `<div class="subsection"><h3>${esc(label)}</h3><div class="rich-text-content">${clean}</div></div>`;
    };

    // REVERTED, per follow-up request: Cost Summary table values are back
    // to plain currency figures (fmt) instead of Millions (fmtM) -- the
    // header cells still say "(currencySymbol)", not "(M)", so the
    // values now match their own column headers again.
    const tableRowsHtml = CATEGORIES.map((c, i) => `
        <tr>
          <td class="label-cell">${c.label}</td>
          <td>${fmt(budgetSeries[i])}${c.percentKey ? `<div class="pct">(${fmtPct(budgetPercentSeries[i])})</div>` : ""}</td>
          <td>${fmt(estimatedSeries[i])}${c.percentKey ? `<div class="pct">(${fmtPct(estimatedPercentSeries[i])})</div>` : ""}</td>
          <td>${fmt(movementSeries[i])}</td>
        </tr>
      `)
      .join("");
    // Per request: add a "Total Cost" row below Escalation in the printed
    // report's Cost Summary table only (the on-screen Dashboard chart-
    // and-table page is untouched) -- reuses the same
    // budgetTotal/estimatedTotal/movement figures already shown in the
    // tiles above this table.
    const totalRowHtml = `
        <tr class="total-row">
          <td class="label-cell"><b>Total Cost</b></td>
          <td><b>${fmt(budgetTotal)}</b></td>
          <td><b>${fmt(estimatedTotal)}</b></td>
          <td><b>${fmt(movement)}</b></td>
        </tr>
      `;

    // ---- Project Overview page ----
    const estDateDisplay = projectExtra?.estimation_date
      ? new Date(projectExtra.estimation_date).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })
      : "";

    // Per follow-up request, all 6 Project Overview fields are now
    // edited with RichTextEditor.jsx (not just Project Introduction), so
    // all 6 are rendered with richTextSection (real HTML) instead of the
    // plain-text section() helper.
    const overviewSectionsHtml = [
      richTextSection("Project Introduction", projectExtra?.intro),
      richTextSection("Financial Overview", projectExtra?.financial),
      richTextSection("Inflation Forecast", projectExtra?.inflation),
      richTextSection("Contingency Summary", projectExtra?.contingency),
      richTextSection("Risk", projectExtra?.risks),
      richTextSection("Opportunity", projectExtra?.opportunities),
    ].join("");
    const hasOverviewContent = !!overviewSectionsHtml;
    const overviewPageHtml = hasOverviewContent
      ? `
        <div class="page page-with-header">
          <table class="page-repeat">
            <thead><tr><th>
              <div class="summary-header">
                <div class="brand"><img src="${logoUrl}" alt="Cost Estimation Tool" /><span>COST ESTIMATION TOOL</span></div>
                <h2>Project Overview</h2>
              </div>
            </th></tr></thead>
            <tbody><tr><td>
              <div class="info-box">
                <div><b>Project Name</b> ${esc(summary.projectName || "—")}</div>
                <div><b>Project ID</b> ${esc(selectedProjectId || "—")}</div>
                <div><b>Operating Company</b> ${esc(summary.operatingCompany || "—")}</div>
                ${projectExtra?.class ? `<div><b>Estimation Class</b> ${esc(projectExtra.class)}</div>` : ""}
                ${projectExtra?.estimated_by ? `<div><b>Estimated By</b> ${esc(projectExtra.estimated_by)}</div>` : ""}
                ${estDateDisplay ? `<div><b>Estimation Date</b> ${esc(estDateDisplay)}</div>` : ""}
              </div>
              ${overviewSectionsHtml}
            </td></tr></tbody>
          </table>
        </div>
      `
      : "";

    // ---- Basis of Cost page ----
    // Sub Elements table -- every saved project_sub_elements row for this
    // project (Class, Asset Group, Asset Element, Main Element, Sub
    // Element, Description, Unit, Quantity, Rate, Cost), placed
    // immediately before the Drawings table per explicit request ("before
    // drawing add project_sub_elements all subelement information in
    // print report").
    // REVERTED, per follow-up request: Rate and Cost are both back to
    // their normal (non-millions) currency-symbol formatting -- the
    // earlier Cost-in-Millions change (fmtCostM) is undone.
    const subElementRowsHtml = (subElementsData || [])
      .map(
        (it) => `
          <tr>
            <td class="label-cell">${esc(it.asset_class || "—")}</td>
            <td>${esc(it.asset_group || "—")}</td>
            <td>${esc(it.asset_element || "—")}</td>
            <td>${esc(it.main_element || "—")}</td>
            <td>${esc(it.sub_element || "—")}</td>
            <td>${esc(it.description || "—")}</td>
            <td>${esc(it.unit || "—")}</td>
            <td>${fmt(it.quantity)}</td>
            <td>${fmt(it.rate)}</td>
            <td>${fmt(it.cost)}</td>
          </tr>
        `
      )
      .join("");
    const subElementsTotalCost = (subElementsData || []).reduce((sum, it) => sum + Number(it.cost || 0), 0);
    // REVERTED, per follow-up request: the printed report's "Estimated
    // Cost" tile again matches the on-screen `estimatedTotal` (summary.
    // estimated.Total_Sum, the saved Flag='E' Cost_Budget_Estimation row)
    // instead of the Sub Elements total below.
    const subElementsSectionHtml = subElementRowsHtml
      ? `<div class="subsection"><h3>Estimated Cost</h3><table class="report-table sub-elements-table"><thead><tr><th>Class</th><th>Asset Group</th><th>Asset Element</th><th>Main Element</th><th>Sub Element</th><th>Description</th><th>Unit</th><th>Quantity</th><th>Rate (${currencySymbol})</th><th>Cost (${currencySymbol})</th></tr></thead><tbody>${subElementRowsHtml}<tr><td class="label-cell" colspan="9" style="text-align:right;">Estimated Cost (${currencySymbol})</td><td>${fmt(subElementsTotalCost)}</td></tr><tr><td class="label-cell" colspan="9" style="text-align:right;">Proposed Cost (${currencySymbol})</td><td>${fmt(budgetSeries[0])}</td></tr></tbody></table></div>`
      : "";

    const drawingRowsHtml = (basisData?.items || [])
      .filter((it) => (it.drawingName || it.DrawingName) || (it.drawingNo || it.DrawingNo) || it.url)
      .map(
        (it) => `
          <tr>
            <td class="label-cell">${esc(it.drawingName || it.DrawingName || "—")}</td>
            <td>${esc(it.drawingNo || it.DrawingNo || "—")}</td>
            <td>${esc(it.url || "—")}</td>
          </tr>
        `
      )
      .join("");

    const blobToDataUrl = (blob) =>
      new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    const attachedDrawingRows = (basisData?.items || []).filter((it) => it.attachedFile);
    const drawingImagesHtml = (
      await Promise.all(
        attachedDrawingRows.map(async (it) => {
          try {
            const res = await axios.get(`${API_BASE}/basis_of_cost_api/files`, {
              params: { basis_id: it.attachedFile.basisId, filename: it.attachedFile.filename },
              responseType: "blob",
            });
            const blob = res.data;
            if (!blob) return "";
            const dataUrl = await blobToDataUrl(blob);
            const caption = it.drawingName || it.DrawingName || it.drawingNo || it.DrawingNo || "Drawing";
            // The "Upload (jpg/pdf)" field on Basis of Cost accepts PDFs too
            // (accept=".pdf,image/*") -- a PDF can't be embedded as an
            // <img>, but the file itself is still right here as a data:
            // URL, so it gets a clickable "View Drawing" link instead of
            // being silently dropped (which is what this used to do for
            // anything that wasn't an image, showing the table row with no
            // indication a file was even attached).
            if (blob.type && blob.type.startsWith("image/")) {
              return `<div class="drawing-image"><img src="${dataUrl}" alt="${esc(caption)}" /><div class="drawing-caption">${esc(caption)}</div></div>`;
            }
            return `<div class="drawing-image"><a class="drawing-file-link" href="${dataUrl}" target="_blank" rel="noopener noreferrer">${esc(caption)} &mdash; View Drawing (PDF)</a></div>`;
          } catch (err) {
            console.error("Failed to load drawing file for print report", err);
            return "";
          }
        })
      )
    ).join("");

    const basisSectionsHtml = [
      section("Assumption", basisData?.assumptions),
      section("Exclusion", basisData?.exclusions),
      subElementsSectionHtml,
      drawingRowsHtml
        ? `<div class="subsection"><h3>Drawings</h3><table class="report-table"><thead><tr><th>Drawing Name</th><th>Drawing No</th><th>URL</th></tr></thead><tbody>${drawingRowsHtml}</tbody></table>${drawingImagesHtml}</div>`
        : "",
    ].join("");
    const hasBasisContent = !!basisSectionsHtml;
    const basisPageHtml = hasBasisContent
      ? `
        <div class="page page-with-header">
          <table class="page-repeat">
            <thead><tr><th>
              <div class="summary-header">
                <div class="brand"><img src="${logoUrl}" alt="Cost Estimation Tool" /><span>COST ESTIMATION TOOL</span></div>
                <h2>Basis of Cost</h2>
              </div>
            </th></tr></thead>
            <tbody><tr><td>
              ${basisSectionsHtml}
            </td></tr></tbody>
          </table>
        </div>
      `
      : "";

    // ---- Elemental Construction Scope page ----
    const CONSTRUCTION_SCOPE_FIELDS = [
      ["FacilitatingWorks", "Facilitating Works"],
      ["Substructure", "Substructure"],
      ["Superstructure", "Superstructure"],
      ["InternalFinishes", "Internal Finishes"],
      ["FittingsFurnishingsEquipment", "Fittings, Furnishings & Equipment"],
      ["Services", "Services"],
      ["CompletedBuildingsUnits", "Completed Buildings & Building Units"],
      ["WorksToExistingBuildings", "Works to Existing Buildings"],
      ["ExternalWorks", "External Works"],
    ];
    // Per follow-up request, these 9 fields are now edited with
    // RichTextEditor.jsx on ElementScopePage1.jsx too, so richTextSection
    // (real HTML) replaces the plain-text section() helper here as well.
    const constructionScopeSectionsHtml = CONSTRUCTION_SCOPE_FIELDS.map(([key, label]) =>
      richTextSection(label, elementScopeData?.[key])
    ).join("");
    const hasConstructionScopeContent = !!constructionScopeSectionsHtml;
    const constructionScopePageHtml = hasConstructionScopeContent
      ? `
        <div class="page page-with-header">
          <table class="page-repeat">
            <thead><tr><th>
              <div class="summary-header">
                <div class="brand"><img src="${logoUrl}" alt="Cost Estimation Tool" /><span>COST ESTIMATION TOOL</span></div>
                <h2>Elemental Construction Scope</h2>
              </div>
            </th></tr></thead>
            <tbody><tr><td>
              ${constructionScopeSectionsHtml}
            </td></tr></tbody>
          </table>
        </div>
      `
      : "";

    // ---- Cover page ----
    const coverHtml = `
      <div class="page cover">
        <div class="cover-decor">
          <div class="ribbon ribbon-diag"></div>
          <div class="ribbon ribbon-fold"></div>
        </div>
        <div class="cover-content">
          <div class="brand">
            <div class="brand-left">
              <img src="${logoUrl}" alt="Cost Estimation Tool" />
              <span>COST ESTIMATION TOOL</span>
              ${
                opcoLogoUrl
                  ? `<span class="brand-sep">|</span><img class="brand-opco-logo" src="${opcoLogoUrl}" alt="${esc(summary.operatingCompany || "OpCo")}" />`
                  : summary.operatingCompany
                  ? `<span class="brand-sep">|</span><span class="brand-client">${esc(summary.operatingCompany)}</span>`
                  : ""
              }
            </div>
            <img class="matarat-logo" src="${mataratLogoUrl}" alt="MATARAT" />
          </div>
          <hr class="divider" />
          <div class="meta">
            <div><b>Project</b> : ${esc(summary.projectName || "—")}</div>
            ${projectExtra?.class ? `<div><b>Estimation</b> : ${esc(projectExtra.class)}</div>` : ""}
            <div><b>Date</b> : ${generatedOn}</div>
          </div>
        </div>
      </div>
    `;

    // ---- Back page ----
    const backPageHtml = `
      <div class="page back-page">
        <div class="cover-decor">
          <div class="ribbon ribbon-diag"></div>
          <div class="ribbon ribbon-fold"></div>
        </div>
        <div class="brand">
          <div class="brand-left">
            <img src="${logoUrl}" alt="Cost Estimation Tool" />
            <span>COST ESTIMATION TOOL</span>
          </div>
          <img class="matarat-logo" src="${mataratLogoUrl}" alt="MATARAT" />
        </div>
        <hr class="divider" />
        <div class="back-footer">
          <span>This report was generated by the Cost Estimation Tool.</span>
          <span>All Rights Reserved &copy; for MATARAT ${reportYear}.</span>
        </div>
      </div>
    `;

    // win was already opened (and given the "Generating report..."
    // placeholder) at the top of this function -- see the FIX comment
    // there for why. document.open() here discards that placeholder
    // document first, so this write() starts a clean document instead of
    // appending onto/alongside the placeholder's own <html>/<body>.
    win.document.open();
    win.document.write(`
      <html>
        <head>
          <title>Cost Estimation Report - ${esc(summary.projectName || "Project")}</title>
          <style>
            /* FIX, per request ("add page no all pages"): a 12mm strip
               is reserved at the bottom of every physical printed page
               for a "Page X of Y" footer -- counter(page)/counter(pages)
               are filled in automatically by the browser's print engine
               for every page it lays out, so this repeats correctly no
               matter how many physical pages a section spills across
               (matching the repeating logo+title header fix above).
               This only shows up in the actual Print dialog/PDF output,
               never in the plain on-screen tab -- that's normal for
               @page rules. .page's own min-height is trimmed from 297mm
               to 285mm right below to match the smaller printable area
               this bottom margin leaves (otherwise every page's content
               would overflow by 12mm and spill an extra near-empty
               page). */
            @page {
              size: A4;
              margin: 0 0 12mm 0;
              @bottom-center {
                content: "Page " counter(page) " of " counter(pages);
                font-family: Arial, Helvetica, sans-serif;
                font-size: 9px;
                color: #888;
              }
            }
            /* Without this, browsers drop every background color/image
               (the cover/back page's dark teal, the KPI tiles, table
               header rows, ...) once the actual Print dialog opens --
               they only show up in the on-screen preview -- unless the
               user manually ticks "Background graphics" in that dialog's
               "More settings". Forcing color-adjust here makes them
               print by default with no extra step needed. */
            * { box-sizing: border-box; -webkit-print-color-adjust: exact; print-color-adjust: exact; color-adjust: exact; }
            /* FIX, per request: the floating "Print / Save as PDF" button
               that used to sit here is removed -- the report tab is a
               normal page, so the browser's own print/save controls
               (Ctrl+P / Cmd+P, or the browser's menu) already cover the
               same thing without an extra on-page button. There is still
               no auto-triggered win.print() call anywhere in this report
               -- see the earlier FIX note on win.focus() below for why
               that matters (a forced print dialog can make the main app
               tab look "stuck" while it's modal). .no-print/@media print
               below are kept in case any other floating/no-print element
               is added later. */
            /* FIX, per request ("direct open PDF format"): each .page
               below is a fixed A4-width sheet (210mm) with no centering,
               so on a normal browser tab -- much wider than 210mm -- it
               sat flush against the left edge with a huge blank white gap
               filling the rest of the window, looking broken/unfinished
               rather than like a report. Centering it on a neutral gray
               background (the same "page on a light tray" look Word/
               Google Docs' own print previews use) makes the on-screen
               tab look like a real document instead of a stray column of
               content; the @media print overrides right below strip all
               of that back out for the actual printed/saved-as-PDF pages,
               which only ever see the plain white .page itself, same as
               before this fix. */
            body { font-family: Arial, Helvetica, sans-serif; margin: 0; color: #212529; background: #e9ecef; }
            /* FIX, per request ("this page need fit in one page when
               printing"): the Executive Summary page's own content
               (header + info box + tiles + chart + an 11-row cost table)
               added up to just over one A4 sheet's printable height, so
               it was spilling a couple of rows onto a second printed
               page instead of staying self-contained. Trimmed here and
               in every rule below marked with the same FIX comment --
               slightly tighter page padding, spacing, and table row
               padding, plus a height cap on the chart image -- so the
               whole page reliably fits on one sheet with room to spare. */
            /* FIX, per follow-up (a genuinely blank "Page 3 of 9" showed
               up between the Executive Summary and Project Overview
               pages in the actual PDF): min-height was trimmed from
               297mm to 285mm above to match the new 12mm-shorter
               printable area (12mm is now reserved at the bottom of
               every page for the "Page X of Y" footer), matching it
               exactly. But the Executive Summary page was already
               tuned right up to the OLD limit ("fits with room to
               spare" was only ever a couple of mm, not a lot) -- and
               min-height forces .page to be at least that tall even
               where its real content is shorter, so the tiny bit of
               empty filler space at the very bottom of that forced box
               has to go somewhere once the printable area shrinks: it
               was spilling onto an otherwise-empty next page. Trimming
               another 2mm off as a safety margin (283mm, not 285mm)
               keeps that forced empty filler comfortably inside the
               real printable area so nothing spills over. */
            .page { width: 210mm; min-height: 283mm; padding: 14mm 16mm; position: relative; page-break-after: always; margin: 24px auto; box-shadow: 0 0 16px rgba(0,0,0,0.15); }
            .page:last-child { page-break-after: auto; }
            @media print {
              .no-print { display: none !important; }
              body { background: #fff; }
              .page { margin: 0; box-shadow: none; }
            }

            /* FIX, per follow-up (cover/back-page background showing a
               gap): the 12mm bottom margin added above for the "Page X
               of Y" footer applies to every physical printed page by
               default, including the cover and the closing back-page --
               but those two are a full-bleed dark background + diagonal
               ribbon design, and a browser's print margin is always
               left unpainted (it's reserved for header/footer text
               only), so that 12mm was cutting a plain white strip
               across the bottom of the design. "page: cover-page"
               assigns both to a named page, and the "@page cover-page"
               rule right below (CSS's mechanism for giving specific
               pages their own page setup) gives just these two their
               old zero-margin, no footer, full-bleed treatment back,
               while every other page still gets the 12mm footer margin
               from the plain @page rule above. min-height is restored
               to the full 297mm here too, since these two don't need
               the 12mm trimmed off for a footer they no longer reserve
               space for. */
            .cover, .back-page { page: cover-page; background: #0d3b3c; color: #fff; overflow: hidden; min-height: 297mm; }
            @page cover-page { size: A4; margin: 0; }
            .cover-content { position: relative; z-index: 2; }
            .cover .brand, .back-page .brand { display: flex; align-items: center; justify-content: space-between; gap: 12px; margin-bottom: 10px; position: relative; z-index: 2; }
            .cover .brand-left, .back-page .brand-left { display: flex; align-items: center; gap: 12px; }
            .cover .brand img, .back-page .brand img { height: 40px; width: auto; }
            .cover .brand span, .back-page .brand span { font-size: 20px; font-weight: 700; letter-spacing: 0.5px; }
            .cover .brand-sep { opacity: 0.5; font-weight: 400; }
            .cover .brand-client { font-size: 16px; }
            .cover .divider, .back-page .divider { border: none; border-top: 1px solid rgba(255,255,255,0.35); margin: 0 0 18px; position: relative; z-index: 2; }
            .cover .meta div { font-size: 18px; margin-bottom: 6px; position: relative; z-index: 2; }
            .cover .meta b { font-weight: 700; }
            /* Per request, replaced the plain thin rotated stripes with a
               bolder "folded ribbon" graphic (a diagonal band crossing the
               upper half, plus a wide zigzag/chevron silhouette filling
               the lower half) matching the reference cover page's
               background -- still no image asset, just two clip-path
               shapes on top of the same dark teal base + white overlay
               tones already used elsewhere on this page. */
            .cover-decor { position: absolute; inset: 0; z-index: 1; overflow: hidden; }
            .cover-decor .ribbon { position: absolute; inset: 0; }
            .cover-decor .ribbon-diag { background: rgba(255,255,255,0.09); clip-path: polygon(100% 19%, 100% 31%, 0% 66%, 0% 54%); }
            .cover-decor .ribbon-fold { background: rgba(255,255,255,0.07); clip-path: polygon(0% 100%, 0% 61%, 46% 50%, 63% 74%, 88% 50%, 100% 61%, 100% 100%); }
            .cover .matarat-logo, .back-page .matarat-logo { height: 40px; width: auto; filter: brightness(0) invert(1); }
            /* OpCo logo (from the OpCo table's Logo column, opco_logo_column.sql)
               replaces the plain OpCo-name text next to "COST ESTIMATION" on
               the cover. Was force-recolored via a CSS filter (brightness(0)
               invert(1)), which flattened an opaque-background logo like
               cluster2's into one indistinguishable white rectangle -- then
               a white "chip" background was tried instead, which fixed
               legibility but left a visible box the logo's own creators
               hadn't put there. FIX, per explicit follow-up request ("remove
               background and Cluster/2 same colour"): toWhiteLogoSilhouette
               (top of file) now pre-processes the logo on a canvas before it
               ever reaches this <img> -- its own background is already
               transparent and every mark (any original color) already
               solid white -- so no chip and no filter are needed here at
               all; the logo just sits directly on the dark cover like the
               MATARAT mark beside it. */
            /* Per request: shrink the OpCo logo (e.g. "Cluster2") on the
               print report cover page -- was height:32px/max-width:140px. */
            .cover .brand-opco-logo { height: 22px; width: auto; max-width: 100px; object-fit: contain; }

            .back-page { display: flex; flex-direction: column; justify-content: flex-end; }
            /* FIX, per request: the two closing lines used to stack on
               separate lines -- now on one row, the credit text pushed to
               the right via space-between, matching the request that
               "All Rights Reserved..." sit on the right side. */
            .back-page .back-footer { display: flex; justify-content: space-between; align-items: baseline; gap: 12px; font-size: 12px; opacity: 0.85; position: relative; z-index: 2; }

            /* FIX (fit-one-page): tighter padding/margin -- see .page above. */
            .summary-header { display: flex; align-items: center; justify-content: space-between; border-bottom: 2px solid #124d52; padding-bottom: 8px; margin-bottom: 14px; }
            .summary-header .brand { display: flex; align-items: center; gap: 10px; }
            .summary-header .brand img { height: 28px; width: auto; }
            .summary-header .brand span { font-weight: 700; font-size: 13px; color: #124d52; }
            .summary-header h2 { font-size: 16px; font-weight: 700; color: #124d52; letter-spacing: 0.5px; margin: 0; text-transform: uppercase; }

            /* FIX, per request ("print show the area name with logo
               print all pages"): each content page's .summary-header
               used to sit directly inside the .page div, so when a
               section's content is taller than one physical sheet, the
               overflow spilled onto extra printed pages with no header
               at all -- it only ever showed once, at the top of that
               section. Wrapping the page's content in a <table> with
               the header inside <thead> fixes this: browsers natively
               repeat a table's <thead> on every physical page it spans
               when printing, so the logo + section name now appears on
               every printed sheet, not just the first one. table.
               page-repeat resets the table/cell styling back to plain
               block layout so this is invisible other than the
               repeating header. */
            table.page-repeat { width: 100%; border-collapse: collapse; }
            table.page-repeat > thead > tr > th, table.page-repeat > tbody > tr > td { padding: 0; text-align: left; font-weight: normal; }

            /* FIX, per follow-up ("give some gap in uper site" -- a
               continuation page's header was sitting flush against the
               paper edge with no breathing room, unlike a section's
               first page): a box's own top padding (.page's 14mm) is
               only re-applied on the FIRST physical page a box lands
               on -- browsers don't repeat top/bottom padding on the
               continuation pages of a box that spills across sheets,
               so .page's 14mm top gap was missing on every page after
               the first one for a page. The fix moves that top gap
               onto the *table's* <thead> instead of the outer .page
               div -- page-with-header turns off .page's own top
               padding, and the same 14mm now lives on the repeating
               header cell instead, so it applies identically on every
               physical page (first and every continuation one),
               matching the first page's look everywhere. */
            .page.page-with-header { padding-top: 0; }
            table.page-repeat > thead > tr > th { padding-top: 14mm; }

            /* FIX (fit-one-page): tighter padding/margin -- see .page above. */
            .info-box { background: #f4f5f7; border-radius: 4px; padding: 10px 18px; margin-bottom: 14px; font-size: 13px; }
            .info-box div { margin-bottom: 4px; }
            .info-box b { display: inline-block; width: 160px; }

            /* FIX (fit-one-page): tighter margin/padding -- see .page above. */
            .tiles { display: flex; gap: 10px; margin-bottom: 14px; }
            .tiles .tile { flex: 1; border-radius: 4px; padding: 9px 8px; text-align: center; color: #fff; }
            .tiles .tile .val { font-size: 15px; font-weight: 700; }
            .tiles .tile .lbl { font-size: 10px; font-weight: 600; margin-top: 4px; }

            /* FIX (fit-one-page), then re-FIXED per explicit request ("chart
               showing very small size"): the original 62mm max-height cap
               sat BELOW the chart's own natural width-constrained height
               (~75mm at this page's ~178mm content width and the chart's
               ~2.37:1 aspect ratio) -- so it was the binding constraint,
               shrinking the chart to noticeably less than full page width
               on top of being short. Raised to 100mm, comfortably above
               that ~75mm natural height, so max-width:100% (not this cap)
               is what actually sizes the chart now -- it renders at the
               page's full content width with proportional height, both
               visibly bigger than before. The rest of this page's content
               (info-box/tiles/11-row table) was re-measured to confirm this
               still leaves headroom to fit one printed A4 sheet. */
            .chart-wrap { margin-bottom: 12px; text-align: center; }
            /* FIX (extra safety margin for the blank-page-3 fix above):
               trimmed another 6mm off the chart's own height cap so
               the Executive Summary page has a bit more headroom to
               reliably stay on one sheet even with the smaller
               printable area. */
            .chart-wrap img { max-width: 100%; max-height: 94mm; width: auto; height: auto; }

            table.report-table { width: 100%; border-collapse: collapse; font-size: 11px; margin-bottom: 8px; }
            /* FIX (fit-one-page): shaved 3px off top+bottom row padding --
               multiplied across the table's 11 cost-category rows plus its
               header row, this is the single biggest space saving here. */
            table.report-table th, table.report-table td { border: 1px solid #dcdcdc; padding: 5px 6px; text-align: center; }
            table.report-table th { background: #124d52; color: #fff; font-size: 10.5px; text-transform: uppercase; }
            table.report-table th:first-child, table.report-table td.label-cell { text-align: left; }
            table.report-table td.label-cell { font-weight: 600; }
            /* FIX (fit-one-page): trimmed margin -- see .page above. */
            table.report-table .pct { font-size: 9px; color: #777; margin-top: 1px; }
            /* Per request: "Total Cost" row appended below Escalation in
               the Cost Summary table (print report only) -- a light tint
               plus a heavier top border sets it apart as the summary line. */
            table.report-table tr.total-row td { background: #eef3f2; border-top: 2px solid #124d52; }

            /* Sub Elements table has 10 columns (Class, Asset Group, Asset
               Element, Main Element, Sub Element, Description, Unit,
               Quantity, Rate, Cost) -- tighter font/padding than the
               standard .report-table so it still fits the 210mm page width
               legibly, with description text wrapping instead of
               overflowing its cell. */
            table.report-table.sub-elements-table { table-layout: fixed; }
            table.report-table.sub-elements-table th,
            table.report-table.sub-elements-table td { padding: 4px 4px; word-break: break-word; }

            .subsection { margin-bottom: 16px; }
            .subsection h3 { font-size: 12.5px; font-weight: 700; color: #124d52; margin: 0 0 6px; text-transform: uppercase; letter-spacing: 0.3px; }
            .subsection p { font-size: 12px; line-height: 1.6; margin: 0; color: #333; }
            .subsection div.text-list { margin: 0; font-size: 12px; line-height: 1.6; color: #333; }
            .subsection div.text-list .text-line { margin-bottom: 4px; }
            .subsection .rich-text-content { font-size: 12px; line-height: 1.6; color: #333; white-space: pre-wrap; }
            .subsection .rich-text-content p, .subsection .rich-text-content div { margin: 0 0 6px; }
            .subsection .rich-text-content ul, .subsection .rich-text-content ol { margin: 0 0 6px; padding-left: 20px; }
            .subsection .rich-text-content li { margin: 0 0 2px; }
            /* FIX, per follow-up ("report not showing all rich text"):
               forcing every <b>/<strong> run to display:block (added
               for the numbered-heading request) was too broad -- it
               also broke any bold WORD or PHRASE in the middle of a
               normal sentence onto its own line, fragmenting ordinary
               paragraphs and making mixed plain/bold text look like it
               was missing content. Bold is back to normal inline
               behavior; headings still land on their own line because
               each one is typed as its own paragraph/div in the editor
               (block-level by default already) and white-space:
               pre-wrap above preserves that structure exactly as
               typed, with no need to force every bold run onto its own
               line. */
            .subsection .rich-text-content b, .subsection .rich-text-content strong { font-weight: 700; }

            .drawing-image { margin-top: 10px; text-align: center; }
            .drawing-image img { max-width: 100%; max-height: 450px; border: 1px solid #dcdcdc; padding: 4px; background: #fff; }
            .drawing-image .drawing-caption { font-size: 10.5px; color: #666; margin-top: 4px; }
            .drawing-file-link { display: inline-block; padding: 8px 16px; border: 1px solid #02bc9c; border-radius: 4px; color: #02bc9c; text-decoration: none; font-size: 12px; font-weight: 600; }
          </style>
        </head>
        <body>
          ${coverHtml}

          <div class="page page-with-header">
            <table class="page-repeat">
              <thead><tr><th>
                <div class="summary-header">
                  <div class="brand">
                    <img src="${logoUrl}" alt="Cost Estimation Tool" />
                    <span>COST ESTIMATION TOOL</span>
                  </div>
                  <h2>Executive Summary</h2>
                </div>
              </th></tr></thead>
              <tbody><tr><td>
                <div class="info-box">
                  <div><b>Project Name</b> ${esc(summary.projectName || "—")}</div>
                  <div><b>Operating Company</b> ${esc(summary.operatingCompany || "—")}</div>
                  <div><b>Project Duration</b> ${esc(formatDurationYears(summary.duration))}</div>
                </div>

                <div class="tiles">
                  <div class="tile" style="background:#3d5166"><div class="val">${esc(summary.operatingCompany || "—")}</div><div class="lbl">COMPANY NAME</div></div>
                  <div class="tile" style="background:#7d5ba6"><div class="val">${esc(formatDurationYears(summary.duration))}</div><div class="lbl">PROJECT DURATION</div></div>
                  <div class="tile" style="background:#147196"><div class="val">${currencySymbol} ${fmtM(budgetTotal)}</div><div class="lbl">PROPOSED COST</div></div>
                  <div class="tile" style="background:#8cc653"><div class="val">${currencySymbol} ${fmtM(estimatedTotal)}</div><div class="lbl">ESTIMATED COST</div></div>
                  <div class="tile" style="background:#c81017"><div class="val">${currencySymbol} ${fmtM(movement)}</div><div class="lbl">VARIANCE</div></div>
                </div>

                <div class="chart-wrap">
                  ${chartImg ? `<img src="${chartImg}" alt="Proposed Vs Estimation chart" />` : ""}
                </div>

                <table class="report-table">
                  <thead>
                    <tr>
                      <th>Cost Summary</th>
                      <th>Proposed Cost (${currencySymbol})</th>
                      <th>Estimated Cost (${currencySymbol})</th>
                      <th>Variance (${currencySymbol})</th>
                    </tr>
                  </thead>
                  <tbody>
                    ${tableRowsHtml}
                    ${totalRowHtml}
                  </tbody>
                </table>
              </td></tr></tbody>
            </table>
          </div>

          ${overviewPageHtml}
          ${basisPageHtml}
          ${constructionScopePageHtml}
          ${backPageHtml}
        </body>
      </html>
    `);
    win.document.close();
    // FIX: win.focus() used to run here, forcing the report tab to the
    // foreground the instant it opened -- per explicit request, the user
    // needs to keep navigating the Cost Estimation Tool itself (sidebar
    // links, other pages) while the report is open, and a stolen-focus
    // tab switch is exactly what got in the way of that: this tab stayed
    // the active/frontmost one until the user manually switched away.
    // Leaving this out means the report opens quietly in a background
    // tab -- this tab never loses focus, so every link and button here
    // keeps working immediately -- and the user can switch to the report
    // tab whenever they actually want to look at or print it.
    //
    // Also no more auto-triggered win.print() here, and no floating
    // "Print / Save as PDF" button either -- the report tab just opens
    // and sits there in the background; printing/saving happens whenever
    // the user chooses, via the browser's own print/save controls
    // (Ctrl+P / Cmd+P, or its menu).
  };

  return (
    <>
      {/* The OpCo / Project Name filter bar that used to render here moved
          up to Dashboard.jsx's tab-pill row (right side) -- see that file.
          selectedOpco/selectedProjectId/projectList/projectLoading are
          now props read from there. */}
      {selectedOpco && !projectLoading && projectList.length === 0 && (
        <div className="alert alert-secondary mb-4">No completed projects found for this Operating Company.</div>
      )}

      {/* Per request, this is no longer just a "please pick something"
          placeholder -- it's now the default landing view, showing every
          completed project's cost estimation details (any OpCo) so
          there's something useful here before drilling into one project
          via the dropdowns above. */}
      {!selectedProjectId && (
        <div className="card border-0 shadow-sm">
          <div className="card-body p-4">
            <h6 className="fw-bold mb-3">All Cost Estimation Details</h6>

            {allSummaryLoading && <p className="text-muted mb-0 py-4 text-center">Loading all cost estimation details...</p>}

            {!allSummaryLoading && allSummaryError && (
              <div className="alert alert-danger mb-0">{allSummaryError}</div>
            )}

            {!allSummaryLoading && !allSummaryError && visibleSummaryRows.length === 0 && (
              <p className="text-muted mb-0 py-4 text-center">
                {selectedOpcoName
                  ? `No completed cost estimations found for ${selectedOpcoName} yet.`
                  : "No completed cost estimations found yet."}
              </p>
            )}

            {!allSummaryLoading && !allSummaryError && visibleSummaryRows.length > 0 && (
              <>
                <div className="table-responsive">
                  {/* Per request, each row is now clickable -- it shows
                      this project's executive summary graph below,
                      exactly as picking its OpCo + Project ID from the
                      dropdowns (now in Dashboard.jsx's header row) already
                      did. onSelectRow is that same OpCo -> Project ID
                      cascade, lifted up to the parent -- see
                      handleSelectProject in Dashboard.jsx. */}
                  <table className="table table-centered align-middle mb-0 dashboard-summary-table">
                    <thead>
                      <tr className="fs-xs fw-bold text-success">
                        <th className="text-start">OpCo</th>
                        <th className="text-start">Project ID</th>
                        <th className="text-start">Project Name</th>
                        <th className="text-end">Proposed Cost</th>
                        <th className="text-end">Estimated Cost</th>
                        <th className="text-end">Variance</th>
                      </tr>
                    </thead>
                    <tbody>
                      {visibleSummaryRows.map((row) => (
                        <tr
                          key={row.projectid}
                          role="button"
                          tabIndex={0}
                          title="View this project's executive summary"
                          onClick={() => onSelectRow(row)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              onSelectRow(row);
                            }
                          }}
                        >
                          <td className="text-start">{row.opco_name || "—"}</td>
                          <td className="text-start">{row.projectid}</td>
                          <td className="text-start">{row.project_name}</td>
                          <td className="text-end">{fmtM(row.budgetTotal)}</td>
                          <td className="text-end">{fmtM(row.estimatedTotal)}</td>
                          <td className="text-end">{fmtM(row.movement)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className="text-muted fs-xs mt-3 mb-0">
                  Select an OpCo and a Project ID above, or click a row below, for that project's detailed Proposed vs Estimation breakdown.
                </p>
              </>
            )}
          </div>
        </div>
      )}

      {selectedProjectId && summaryLoading && (
        <div className="card border-0 shadow-sm">
          <div className="card-body text-center py-5">
            <p className="text-muted mb-0">Loading executive summary...</p>
          </div>
        </div>
      )}

      {selectedProjectId && !summaryLoading && summaryError && (
        <div className="card border-0 shadow-sm">
          <div className="card-body">
            <div className="alert alert-danger mb-0">{summaryError}</div>
          </div>
        </div>
      )}

      {selectedProjectId && !summaryLoading && !summaryError && summary && (
        <>
          {/* Per request, same "Print Report" button as the per-project
              EstimationSummary.jsx page -- same styling, above the
              Construction (wizard step) bar since there's no breadcrumb
              row on this Dashboard tab to sit below. */}
          <div className="d-flex justify-content-end mb-3">
            <button
              type="button"
              className="btn btn-sm text-white px-3 py-2 fw-semibold d-inline-flex align-items-center gap-2"
              style={{ backgroundColor: "#124d52", borderRadius: 2 }}
              onClick={handlePrintReport}
            >
              <i className="ti ti-printer fs-5" />
              Print Report
            </button>
          </div>

          {/* Step indicator bar -- same all-green, clickable "jump to any
              step" bar as EstimationSummary.jsx (see WIZARD_STEPS/
              STEP_ROUTES above), opening each step's read-only page. */}
          <div className="mb-4">
            <div className="progress mb-3" style={{ height: 6 }}>
              <div className="progress-bar wizard-progress-bar" style={{ width: "100%" }} />
            </div>
            <ul className="nav nav-tabs wizard-tabs w-100 row g-2 border-0" role="tablist">
              {WIZARD_STEPS.map((step) => (
                <li className="nav-item col-12 col-lg" key={step.key}>
                  <span
                    className="nav-link h-100 wizard-item-done"
                    role="button"
                    tabIndex={0}
                    style={{ cursor: "pointer" }}
                    onClick={() => goToStep(step.key)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        goToStep(step.key);
                      }
                    }}
                  >
                    <span className="d-flex align-items-center">
                      {step.key === "budget" ? (
                        <svg
                          className="flex-shrink-0"
                          style={{ width: "32px", height: "32px" }}
                          viewBox="0 0 917 1025"
                          fill="currentColor"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path fillRule="evenodd" d="M 916 834 L 570 908 L 550 964 L 542 1001 L 540 1024 L 884 951 L 893 932 L 904 899 L 912 865 Z M 646 54 L 539 144 L 539 473 L 435 496 L 430 1 L 324 89 L 324 518 L 83 570 L 51 682 L 323 629 L 324 767 L 32 830 L 0 946 L 310 881 L 346 863 L 428 746 L 437 604 L 539 584 L 539 803 L 885 730 L 916 614 L 646 670 L 646 561 L 885 509 L 916 397 L 646 449 Z" />
                        </svg>
                      ) : (
                        <i className={`ti ${step.icon} fs-32 flex-shrink-0`} />
                      )}
                      <span className="flex-grow-1 ms-2 text-truncate">
                        <span className="mb-0 lh-base d-block fw-semibold text-body fs-base">{step.label}</span>
                        <span className="fs-xxs mb-0">{step.sub}</span>
                      </span>
                    </span>
                  </span>
                </li>
              ))}
            </ul>
          </div>

          {/* Separator between the wizard step bar and the KPI tiles below
              -- per request, makes the two sections visually distinct
              instead of running straight into each other. Explicit
              dark-gray border color (same as EstimationSummary.jsx's
              matching separator) rather than Bootstrap's own faint
              default <hr>. */}
          <hr className="my-4" style={{ borderTop: "1px solid #6c757d" }} />

          {/* Per request, shows which project this summary/wizard bar
              belongs to -- looked up from the same projectList the
              dropdown in Dashboard.jsx uses (matched on projectid),
              since the summary payload itself has no name field. */}
          <div className="mb-3 fw-semibold text-dark">
            Project Name : {projectList.find((p) => String(p.projectid) === String(selectedProjectId))?.project_name || "—"}
          </div>

          {/* Same 5-tiles-in-one-row treatment as the per-project
              EstimationSummary.jsx page: OpCo + Duration ahead of the 3
              cost tiles, row-cols-lg-5 instead of a 12-column col-* split
              since 5 doesn't divide evenly into 12. operatingCompany is
              already resolved server-side (OpCo_ID code -> OpCo_Name) by
              get_project_estimations_api in app.py. */}
          <div className="row row-cols-1 row-cols-sm-2 row-cols-md-3 row-cols-lg-5 g-3 mb-4">
            <div className="col">
              <div className="p-3 text-center text-white rounded h-100 d-flex flex-column justify-content-center" style={{ backgroundColor: "#3d5166" }}>
                <div className="fs-4 fw-bold lh-1 text-truncate" title={summary.operatingCompany || "—"}>
                  {summary.operatingCompany || "—"}
                </div>
                {/* Per request, relabeled from "OpCo" to "Company Name",
                    same as EstimationSummary.jsx's matching tile. */}
                <div className="fs-xs fw-semibold mt-1">Company Name</div>
              </div>
            </div>
            <div className="col">
              <div className="p-3 text-center text-white rounded h-100 d-flex flex-column justify-content-center" style={{ backgroundColor: "#7d5ba6" }}>
                <div className="fs-4 fw-bold lh-1">{formatDurationYears(summary.duration)}</div>
                {/* Per request, relabeled from "Duration" to "Project
                    Duration", same as EstimationSummary.jsx's tile. */}
                <div className="fs-xs fw-semibold mt-1">Project Duration</div>
              </div>
            </div>
            {/* Per request, all 5 tiles' value text was bumped from fs-5
                back up to fs-4 for better legibility, keeping them all at
                the same scale (Company Name/Duration tiles bumped
                alongside the 3 cost tiles, not just the cost ones). */}
            <div className="col">
              <div className="p-3 text-center text-white rounded h-100 d-flex flex-column justify-content-center" style={{ backgroundColor: "#147196" }}>
                <div className="fs-4 fw-bold lh-1">{currencySymbol} {fmtM(budgetTotal)}</div>
                <div className="fs-xs fw-semibold mt-1">Proposed Cost</div>
              </div>
            </div>
            <div className="col">
              <div className="p-3 text-center text-white rounded h-100 d-flex flex-column justify-content-center" style={{ backgroundColor: "#8cc653" }}>
                <div className="fs-4 fw-bold lh-1">{currencySymbol} {fmtM(estimatedTotal)}</div>
                <div className="fs-xs fw-semibold mt-1">Estimated Cost</div>
              </div>
            </div>
            <div className="col">
              <div className="p-3 text-center text-white rounded h-100 d-flex flex-column justify-content-center" style={{ backgroundColor: "#c81017" }}>
                <div className="fs-4 fw-bold lh-1">{currencySymbol} {fmtM(movement)}</div>
                <div className="fs-xs fw-semibold mt-1">Variance</div>
              </div>
            </div>
          </div>

          {/* Per request, widened further -- side padding removed entirely
              (was p-3/p-md-4, then px-2/px-md-3) so the canvas (responsive:
              true, fills 100% of this card's content box) now spans the
              exact same full width as the KPI tiles row and data table
              above/below it, instead of sitting inset within its own card
              padding. Title gets its own small side padding (px-2) so
              "Proposed Vs Estimation" doesn't sit flush against the card's
              rounded edge. */}
          <div className="card border-0 shadow-sm rounded py-3 py-md-4 px-0 mb-4">
            <h6 className="fw-bold mb-3 px-2">Proposed Vs Estimation</h6>
            {/* Per request, enlarged further -- 340px -> 480px -> 640px -> 760px --
                taller chart reads more clearly, especially the smaller bars
                (Risk, Inflation, etc.) next to Execution's much larger one. */}
            <div style={{ height: 760 }}>
              <Bar ref={chartRef} data={chartData} options={chartOptions} />
            </div>
          </div>

          <div className="card border rounded p-3 mb-4">
            <div className="table-responsive">
              <table className="table table-centered align-middle text-center mb-0">
                <thead>
                  <tr className="fs-xs fw-bold text-success">
                    <th></th>
                    {CATEGORIES.map((c) => (
                      <th key={c.key}>{c.label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="text-start fw-semibold">
                      <span className="d-inline-block rounded-circle me-2" style={{ width: 10, height: 10, background: COLOR_BUDGET }} />
                      Proposed Cost
                    </td>
                    {budgetSeries.map((v, i) => (
                      <td key={i}>
                        {fmtM(v)}
                        {CATEGORIES[i].percentKey && <div className="fs-xxs text-muted mt-1">({fmtPct(budgetPercentSeries[i])})</div>}
                      </td>
                    ))}
                  </tr>
                  <tr>
                    <td className="text-start fw-semibold">
                      <span className="d-inline-block rounded-circle me-2" style={{ width: 10, height: 10, background: COLOR_ESTIMATED }} />
                      Estimated Cost
                    </td>
                    {estimatedSeries.map((v, i) => (
                      <td key={i}>
                        {fmtM(v)}
                        {CATEGORIES[i].percentKey && <div className="fs-xxs text-muted mt-1">({fmtPct(estimatedPercentSeries[i])})</div>}
                      </td>
                    ))}
                  </tr>
                  <tr>
                    <td className="text-start fw-semibold">
                      <span className="d-inline-block rounded-circle me-2" style={{ width: 10, height: 10, background: COLOR_MOVEMENT }} />
                      Variance
                    </td>
                    {movementSeries.map((v, i) => (
                      <td key={i}>{fmtM(v)}</td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </>
  );
}
